// PATH: src/pages/ReferencesPage.tsx — registre des références de documents
// officiels (certificats, attestations, reçus). Réservé à la direction (backend).
//
// Trois blocs : analytique (KPIs), recherche/lookup, et la liste des références
// avec un lien « Ouvrir » qui rejoue le document. L'attribution du numéro se
// fait côté backend à la génération du document — cette page est en lecture.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Card, Col, Empty, Input, Result, Row, Segmented, Space, Statistic, Tag, Typography, message,
} from "antd";
import { SearchOutlined, PrinterOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { useApiClient } from "../api/apiClients";
import PageHeader from "../components/PageHeader";
import ResponsiveTable from "../components/ResponsiveTable";

interface DocRef {
  id: number;
  doc_type: string;
  reference_no: string;
  year: number;
  student_id: number;
  student_name: string;
  enrollment_id: number;
  period_id: number; // reçu : période ; 0 sinon
  amount?: number;
  issued_at: string;
}

interface RefStats {
  total: number;
  by_type: Record<string, number>;
  receipts_count: number;
  receipts_amount: number;
  this_year_count: number;
}

// Libellés + préfixes : la légende de la page et l'affichage des lignes partagent
// la même source (les préfixes doivent rester alignés sur refPrefix côté backend).
const DOC_TYPES: Record<string, { label: string; prefix: string; color: string }> = {
  CERTIFICAT_SCOLARITE: { label: "Certificat de scolarité", prefix: "CS", color: "blue" },
  ATTESTATION_INSCRIPTION: { label: "Attestation d'inscription", prefix: "AI", color: "geekblue" },
  RECU_PAIEMENT: { label: "Reçu de paiement", prefix: "RP", color: "green" },
};

const money = (v?: number) => (v != null ? `${new Intl.NumberFormat("fr-MA").format(v)} DH` : "—");

// Lien de réouverture du document imprimable. Le reçu porte sa période ; les
// autres documents s'en passent.
const printHref = (r: DocRef) =>
  `/enrollments/${r.enrollment_id}/print?doc_type=${r.doc_type}` +
  (r.period_id ? `&period_id=${r.period_id}` : "");

const ReferencesPage: React.FC = () => {
  const http = useApiClient("tilmischool");
  const [stats, setStats] = useState<RefStats | null>(null);
  const [items, setItems] = useState<DocRef[]>([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [docType, setDocType] = useState<string>("");
  // q = ce qui est interrogé (différé) ; searchText = ce qui est affiché.
  const [searchText, setSearchText] = useState("");
  const [q, setQ] = useState("");

  const loadStats = useCallback(() => {
    http.get<RefStats>("/api/v1/school/references/stats")
      .then((r) => { setStats(r.data); setForbidden(false); })
      .catch((err) => { if (err?.response?.status === 403) setForbidden(true); });
  }, [http]);

  const loadList = useCallback(() => {
    setLoading(true);
    http.get<DocRef[]>("/api/v1/school/references", {
      params: { doc_type: docType || undefined, q: q || undefined },
    })
      .then((r) => { setItems(r.data ?? []); setForbidden(false); })
      .catch((err) => {
        if (err?.response?.status === 403) setForbidden(true);
        else message.error("Impossible de charger les références");
      })
      .finally(() => setLoading(false));
  }, [http, docType, q]);

  useEffect(loadStats, [loadStats]);
  useEffect(loadList, [loadList]);

  // Recherche différée : une requête par frappe saturerait l'API. Le timer vit
  // dans une ref, sinon il serait recréé (et jamais annulé) à chaque rendu.
  const debounce = useRef<ReturnType<typeof setTimeout>>();
  const onSearch = (value: string) => {
    setSearchText(value);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => setQ(value), 300);
  };
  useEffect(() => () => clearTimeout(debounce.current), []);

  const kpis = useMemo(() => {
    const t = stats?.by_type ?? {};
    return {
      total: stats?.total ?? 0,
      recus: stats?.receipts_count ?? 0,
      recusAmount: stats?.receipts_amount ?? 0,
      cs: t.CERTIFICAT_SCOLARITE ?? 0,
      ai: t.ATTESTATION_INSCRIPTION ?? 0,
      thisYear: stats?.this_year_count ?? 0,
    };
  }, [stats]);

  if (forbidden) {
    return <Result status="403" title="Registre réservé à la direction de l'établissement" />;
  }

  return (
    <div>
      <PageHeader
        title="Références"
        subtitle="Registre des documents officiels délivrés (certificats, attestations, reçus)"
      />

      {/* ── Analytique ─────────────────────────────────────────────────────── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small"><Statistic title="Références délivrées" value={kpis.total} /></Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="Reçus de paiement"
              value={kpis.recus}
              suffix={<span style={{ fontSize: 13, color: "var(--app-muted)" }}>· {money(kpis.recusAmount)}</span>}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small"><Statistic title="Certificats · Attestations" value={`${kpis.cs} · ${kpis.ai}`} /></Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small"><Statistic title="Cette année" value={kpis.thisYear} /></Card>
        </Col>
      </Row>

      {/* ── Légende : « à quoi servent les références » ─────────────────────── */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap size={16}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>Préfixes :</Typography.Text>
          {Object.entries(DOC_TYPES).map(([, d]) => (
            <Space key={d.prefix} size={6}>
              <Tag bordered={false} color={d.color} style={{ fontFamily: "monospace" }}>{d.prefix}</Tag>
              <Typography.Text style={{ fontSize: 13 }}>{d.label}</Typography.Text>
            </Space>
          ))}
        </Space>
      </Card>

      {/* ── Recherche / lookup + filtre type ───────────────────────────────── */}
      <Card styles={{ body: { padding: 16 } }} style={{ marginBottom: 16 }}>
        <Space wrap size={8}>
          <Input
            placeholder="Rechercher une référence ou un élève"
            allowClear
            prefix={<SearchOutlined style={{ color: "var(--app-muted)" }} />}
            style={{ width: 320 }}
            value={searchText}
            onChange={(e) => onSearch(e.target.value)}
          />
          <Segmented
            value={docType}
            onChange={(v) => setDocType(v as string)}
            options={[
              { value: "", label: "Tous" },
              { value: "RECU_PAIEMENT", label: "Reçus" },
              { value: "CERTIFICAT_SCOLARITE", label: "Certificats" },
              { value: "ATTESTATION_INSCRIPTION", label: "Attestations" },
            ]}
          />
        </Space>
      </Card>

      {/* ── Registre ───────────────────────────────────────────────────────── */}
      <Card styles={{ body: { padding: 0 } }}>
        <ResponsiveTable<DocRef>
          rowKey="id"
          loading={loading}
          card={{
            title: (r) => <span style={{ fontFamily: "monospace" }}>{r.reference_no}</span>,
            extra: (r) => {
              const d = DOC_TYPES[r.doc_type];
              return d ? <Tag bordered={false} color={d.color}>{d.label}</Tag> : <Tag>{r.doc_type}</Tag>;
            },
            description: (r) => (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {r.student_name} · {dayjs(r.issued_at).format("DD/MM/YYYY")}
                {r.amount != null ? ` · ${money(r.amount)}` : ""}
              </Typography.Text>
            ),
            actions: (r) => [
              <Link key="open" to={printHref(r)}>
                <Space size={4}><PrinterOutlined /> Ouvrir</Space>
              </Link>,
            ],
          }}
          dataSource={items}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucune référence délivrée" /> }}
          pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} référence${t > 1 ? "s" : ""}`, style: { padding: "0 16px" } }}
          columns={[
            {
              title: "Référence",
              dataIndex: "reference_no",
              width: 190,
              render: (v: string) => <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{v}</span>,
            },
            {
              title: "Type",
              dataIndex: "doc_type",
              width: 200,
              render: (v: string) => {
                const d = DOC_TYPES[v];
                return d ? <Tag bordered={false} color={d.color}>{d.label}</Tag> : v;
              },
            },
            { title: "Élève", dataIndex: "student_name" },
            {
              title: "Montant",
              dataIndex: "amount",
              width: 130,
              align: "right",
              render: (v?: number) => (v != null ? money(v) : <span style={{ color: "var(--app-faint)" }}>—</span>),
            },
            {
              title: "Délivré le",
              dataIndex: "issued_at",
              width: 140,
              render: (v: string) => <span style={{ fontFamily: "monospace", fontSize: 13 }}>{dayjs(v).format("DD/MM/YYYY")}</span>,
            },
            {
              title: "",
              width: 110,
              render: (_, r) => (
                <Link to={printHref(r)}>
                  <Space size={4}><PrinterOutlined /> Ouvrir</Space>
                </Link>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default ReferencesPage;
